import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Close,
  AccountBalanceOutlined,
  PhotoCameraOutlined,
  CheckRounded,
} from "@mui/icons-material";
import { branchCashDeposit } from "api/branchCash";
import { depositBranchCash } from "api/financial";

const CURRENCIES = ["THB", "LAK", "USD"];
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const MONO = "'Space Grotesk', ui-monospace, monospace";
const money = (n, c = "THB") => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// Branch staff hands physical cash to the bank: pick station + currency + amount and
// attach the bank deposit slip. The drawer balance for the chosen station/currency is
// shown so they don't over-deposit.
// `mode="branch"` (default) is the station-scoped self-service deposit (/api/branch-cash,
// only the user's own station). `mode="financial"` is the manager path from the Financial
// drill-down (/api/financial/cash-deposit, any station) — same UI, different endpoint.
export default function BranchCashDepositDialog({ open, onClose, stations = [], onSuccess, mode = "branch" }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  const [stationId, setStationId] = useState("");
  const [currency, setCurrency] = useState("THB");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setStationId(stations[0]?.stationId || "");
      setCurrency("THB");
      setAmount("");
      setFile(null);
      setError("");
      setSubmitting(false);
    }
  }, [open, stations]);

  const station = useMemo(() => stations.find((s) => s.stationId === stationId) || null, [stations, stationId]);
  const available = station ? Number(station[currency]) || 0 : 0;
  const amt = Number(amount) || 0;
  const overBalance = amt > available;

  const handleSubmit = async () => {
    if (!stationId || !amt || amt <= 0) return setError(t("branchCash.invalidAmount"));
    if (!file) return setError(t("branchCash.slipRequired"));
    if (overBalance) return setError(t("branchCash.overBalance"));
    setSubmitting(true);
    setError("");
    const fd = new FormData();
    fd.append("stationId", stationId);
    fd.append("currency", currency);
    fd.append("amount", String(amt));
    fd.append("evidence_0", file);
    const res = mode === "financial" ? await depositBranchCash(fd) : await branchCashDeposit(fd);
    setSubmitting(false);
    if (res && res.success) {
      onSuccess && onSuccess(res);
    } else {
      setError((res && res.message) || t("common.error"));
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: alpha(accent, 0.1), color: accent, flexShrink: 0 }}>
              <AccountBalanceOutlined />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.15 }}>{t("branchCash.depositTitle")}</Typography>
              <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{t("branchCash.depositSubtitle")}</Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={submitting}><Close fontSize="small" /></IconButton>
        </Box>

        <Stack spacing={1.75} sx={{ mt: 2.5 }}>
          {stations.length > 1 && (
            <TextField select fullWidth size="small" color="secondary" label={t("branchCash.station")} value={stationId} onChange={(e) => setStationId(e.target.value)}>
              {stations.map((s) => (
                <MenuItem key={s.stationId} value={s.stationId}>{s.stationName}</MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction="row" spacing={1.5}>
            <TextField select size="small" color="secondary" label={t("branchCash.currency")} value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 120 }}>
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" type="number" color="secondary" label={t("branchCash.amount")} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Stack>

          {/* available balance */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.5, py: 1.25, borderRadius: "12px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
            <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{t("branchCash.availableInDrawer")}</Typography>
            <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 15 }} color={overBalance ? "error.main" : "text.primary"}>
              {money(available, currency)}
            </Typography>
          </Box>

          {/* slip upload */}
          <Button variant={file ? "contained" : "outlined"} color="secondary" component="label" startIcon={file ? <CheckRounded /> : <PhotoCameraOutlined />} sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none", justifyContent: "flex-start", py: 1.25 }}>
            {file ? t("branchCash.slipAttached") : t("branchCash.attachSlip")}
            <input hidden accept="image/*" type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
          </Button>

          {overBalance && <Alert severity="warning" sx={{ borderRadius: "12px" }}>{t("branchCash.overBalance")}</Alert>}
          {error && <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={submitting} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={submitting || overBalance || !amt || !file} sx={{ borderRadius: "12px", fontWeight: 700, px: 3 }}>
          {t("branchCash.confirmDeposit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
